// Affiliate referral integration. Two modes, both feeding the Stripe
// checkout via getReferralId():
//
//  1. Affonso (when VITE_AFFONSO_PROGRAM_ID is set) — loads Affonso's pixel,
//     which captures ?via=/?ref= and stores the referral in a first-party
//     `affonso_referral` cookie. The server stamps that value into the Stripe
//     session metadata as `affonso_referral`, which is how Affonso attributes
//     the sale (their Stripe integration reads it off checkout.session.completed).
//
//  2. Manual / platform-free (no env key, and also as a backup when the pixel
//     is blocked) — a generic ?ref= (or ?via=) param is captured into a 30-day
//     first-party cookie, so the program can be run BY HAND (read
//     client_reference_id off the Stripe checkout session). Fully inert until
//     someone arrives with a ?ref= link; never changes checkout behavior when
//     there's no referral.

const env = import.meta.env as Record<string, string | undefined>;
const AFFONSO_PROGRAM_ID = env.VITE_AFFONSO_PROGRAM_ID;
// Must match the referral window configured in the Affonso dashboard (and the
// cookie window promised on /affiliate-program).
const AFFONSO_COOKIE_DAYS = env.VITE_AFFONSO_COOKIE_DAYS || "30";

const REF_COOKIE = "solmi_ref";
// Keep in sync with the Affonso cookie window + the /affiliate-program copy.
const REF_MAX_AGE = 30 * 24 * 60 * 60; // 30-day cookie, in seconds

let injected = false;

function injectAffonso(): void {
  if (injected || !AFFONSO_PROGRAM_ID) return;
  if (typeof document === "undefined" || typeof window === "undefined") return;
  injected = true;
  const s = document.createElement("script");
  s.async = true;
  s.defer = true;
  s.src = "https://cdn.affonso.io/js/pixel.min.js";
  s.setAttribute("data-affonso", AFFONSO_PROGRAM_ID);
  s.setAttribute("data-cookie_duration", AFFONSO_COOKIE_DAYS);
  document.head.appendChild(s);
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${name}=([^;]+)`),
  );
  if (!m) return undefined;
  // A malformed %-sequence (hand-edited or third-party-written cookie) makes
  // decodeURIComponent throw; fall back to the raw value rather than crashing
  // the buy click.
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

// Capture ?ref= / ?via= into a first-party cookie on landing. First-touch —
// does not overwrite an existing referral. Runs in both modes: with Affonso
// active it's a backup trail (client_reference_id) for manual auditing when
// the pixel is blocked by content blockers.
function captureRefParam(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  try {
    const p = new URLSearchParams(window.location.search);
    const code = p.get("ref") || p.get("via");
    if (code && !readCookie(REF_COOKIE)) {
      const safe = encodeURIComponent(code.slice(0, 200));
      document.cookie = `${REF_COOKIE}=${safe}; max-age=${REF_MAX_AGE}; path=/; SameSite=Lax`;
    }
  } catch {
    /* no-op */
  }
}

// Runs once when this module is first imported (App imports it app-wide).
injectAffonso();
captureRefParam();

/**
 * The affiliate referral id to send to the server as `clientReferenceId`.
 * The server sets it as Stripe `client_reference_id` AND as session metadata
 * `affonso_referral` (Affonso's attribution key). In Affonso mode this is the
 * pixel's `affonso_referral` cookie; otherwise the captured ?ref= code.
 * Returns undefined when there's no referral, leaving checkout unaffected.
 */
export function getReferralId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const id = readCookie("affonso_referral") || readCookie(REF_COOKIE);
  // Cap to the server's z.string().max(200) — an oversized third-party cookie
  // must degrade to a truncated referral, not hard-fail the checkout mutation.
  return id ? id.slice(0, 200) : undefined;
}

/**
 * The captured ?ref= code for the IN-APP referral program ("invite friends,
 * earn credits") — deliberately reads only the manual solmi_ref cookie, never
 * the Affonso pixel cookie (that value is an Affonso id, not a user referral
 * code). The server silently ignores codes that don't resolve to a user, so
 * an affiliate slug sharing this cookie is harmless.
 */
export function getInAppReferralCode(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const code = readCookie(REF_COOKIE);
  return code ? code.slice(0, 200) : undefined;
}

/**
 * Report a completed signup to Affonso so affiliates see referred signups
 * (not just purchases) in their dashboard. No-op when the pixel isn't loaded
 * (no VITE_AFFONSO_PROGRAM_ID, blocked by content blockers) or when the
 * visitor has no referral — Affonso ignores unreferred signups.
 */
export function trackAffiliateSignup(email?: string | null): void {
  if (typeof window === "undefined") return;
  try {
    const affonso = (
      window as unknown as { Affonso?: { signup?: (email?: string) => void } }
    ).Affonso;
    affonso?.signup?.(email || undefined);
  } catch {
    /* no-op */
  }
}
